import { take, takeEvery, call, put, race, all, select } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { stopSubmit, startSubmit } from 'redux-form';

import history from '../services/history';
import * as api from '../services/api';
import { TimeoutException } from '../services/exceptions';

import HOOK from "../components/hooks"
import { selectors as userSelectors, actions as userActions } from './userModule';
import { getUrlTrialDaysLeft } from "../modules/trialCode"
import { ACTIVITY_STATUS, BASE, JARGON, PATH } from '../modules/constants'
import { CHECKBOX, CHECKBOX_GROUP_PROPS, CHECKBOX_LEVEL, resetCheckboxDefaults } from '../modules/checkbox_constants'

const TOKEN_KEY = 'numerable_token_v1';
const LOGOUT_WARNING_TIME_SECONDS = 60

// Returns { [hook]: status } - status true means "open". A group starts closed only if its default
// state has every checkbox selected - if some or none are selected by default, open it so that's
// visible without a click. Same rule in both portrait and landscape.
export const defaultRollupStatus = () => {
  const status = {}

  for (const [_, value] of Object.entries(HOOK)) {
    const items = CHECKBOX[value]
    const fullBitmask = items ? items.reduce((sum, item) => sum + item.BIT, 0) : 0
    const allSelectedByDefault = !items || fullBitmask === (CHECKBOX.DEFAULTS[value] ?? 0)
    status[value] = !allSelectedByDefault
  }
  return status
}

const initRollups = () => {
  // Initialise the object that keeps track of rollups/collapse state of checkbox groups
  const rollups = {}
  const status = defaultRollupStatus()

  for (const [_, value] of Object.entries(HOOK))
    rollups[value] = { id: value, status: status[value] }
  return rollups
}

const initialState = {
  activityStatus: ACTIVITY_STATUS.ACTIVE,
  features: {},
  loaded: false,
  networkStatus: 'UP',
  page: 'HomePage',
  // MC put back initialisation to fix 350 club bug
  specialUserFeatures: {
    CheatSheet: true,
    EnablePosition: true,
    ExportCsv: true,
    ExternalLinks: true,
    LseAnalysis: true,
    LseAppts: true,
    MaxLseData: true,
    PremiumCheckboxes: true,
    ShowLogos: true,
    ShowLseDataCard: true,
    StandardCheckboxes: true,
    TimeLine: true,
  },
  // specialUserFeatures: {},
  rollups: initRollups(),
  timeToLogout: LOGOUT_WARNING_TIME_SECONDS,
  idleLoggedOut: false,
};

export function reducer(state = initialState, action) {
  switch (action.type) {

    case 'app.login.start': {
      // console.log('app.login.start')
      return {
        ...state,
        loginError: null,
        activityStatus: ACTIVITY_STATUS.ACTIVE,
        timeToLogout: LOGOUT_WARNING_TIME_SECONDS,
      }
    }

    case 'app.idleLoggedOut': {
      // console.log('app.idleLoggedOut action.status:', action.status)
      return { ...state, idleLoggedOut: action.status };
    }

    case 'app.login.error': {
      return { ...state, loginError: action.error };
    }

    case 'app.register.start': {
      return { ...state, registerError: null };
    }

    case 'app.register.error': {
      return { ...state, registerError: action.error };
    }

    case 'app.confirmAccount.start': {
      return { ...state, resendResult: null, confirmError: null };
    }

    case 'app.confirmAccount.error': {
      return { ...state, confirmError: action.error };
    }

    case 'app.forgotPassword.start': {
      return { ...state, forgotError: null };
    }

    case 'app.forgotPassword.error': {
      return { ...state, forgotError: action.error };
    }

    case 'app.userDetailsChanged.start': {
      return { ...state, userDetailsChangedError: null };
    }

    case 'app.userDetailsChanged.error': {
      return { ...state, userDetailsChangedError: action.error };
    }

    case 'app.setNewPassword.start': {
      return { ...state, setNewPassError: null };
    }

    case 'app.setNewPassword.error': {
      return { ...state, setNewPassError: action.error };
    }

    case 'app.resendResetCode.start': {
      return { ...state, resendResetResult: null, setNewPassError: null };
    }

    case 'app.resendResetCode.result': {
      return { ...state, resendResetResult: action.resultMsg };
    }

    case 'app.resendCode.start': {
      return { ...state, resendResult: null, confirmError: null };
    }

    case 'app.resendCode.result': {
      return { ...state, resendResult: action.resultMsg };
    }

    case 'app.saveToken': {
      // console.log('app.saveToken action:', action)
      const features = action.token.features.reduce((o, f) => { o[f] = true; return o; }, {})
      return {
        ...state,
        token: action.token,
        features,
      }
    }

    case 'app.clearToken': {
      // console.log('clearToken action:', action)
      // Clear user features and token, but retain any 'special user' features
      const features = action.the350club || action.urlTrial ? state.specialUserFeatures : {}
      return {
        ...state,
        token: null,
        features,
      };
    }

    case 'app.appendFeatures': {
      // console.log('appendFeatures action:', action)
      var features = { ...state.features, ...action.features }
      return {
        ...state,
        features,
      };
    }

    case 'app.initSpecialUserFeatures': {
      // console.log('initSpecialUserFeatures action:', action)
      return {
        ...state,
        // Convert the feature list {"f1", "f2" etc} to {f1: true, f2: true etc}
        specialUserFeatures: action.specialUserFeatures.reduce((o, f) => { o[f] = true; return o; }, {})
      };
    }

    case 'app.appendSpecialUserFeatures': {
      // console.log('appendSpecialUserFeatures action:', action)
      const features = { ...state.features, ...state.specialUserFeatures }
      return {
        ...state,
        features,
      };
    }

    case 'app.saveUserLogin': {
      return { ...state, usr: action.email, pwd: action.password };
    }

    case 'app.clearUserLogin': {
      return { ...state, pwd: null };
    }

    case 'app.loaded': {
      return { ...state, loaded: true };
    }

    case 'app.network.up': {
      return { ...state, networkStatus: 'UP' };
    }

    case 'app.network.down': {
      return { ...state, networkStatus: 'DOWN' };
    }

    case 'app.network.pending': {
      return { ...state, networkStatus: state.networkStatus === 'UP' ? 'UP' : 'PENDING' };
    }

    case 'app.activity.active': {
      // console.log('app.activity.active')
      return { ...state, activityStatus: ACTIVITY_STATUS.ACTIVE }
    }

    case 'app.activity.idle': {
      // console.log('app.activity.idle')
      return { ...state, activityStatus: ACTIVITY_STATUS.IDLE }
    }

    case 'app.logout.seconds': {
      // console.log('app.logout.seconds: action.logoutSeconds', action.logoutSeconds)
      return { ...state, timeToLogout: action.logoutSeconds }
    }

    case 'app.setPage': {
      // console.log('app.setPage action:', action)
      if (!action.page)
        throw new Error('app.setPage: action.page is undefined or null')

      return {
        ...state,
        page: action.page,
      }
    }

    case 'app.getSpecialUserFeatures': {
      // console.log('app.getSpecialUserFeatures action:', action)
      return state
    }

    case 'app.toggleRollup': {
      // console.log('%capp.toggleRollup action', "background: #cef0a8ff", action)
      const rollups = { ...state.rollups }
      rollups[action.group].status = !rollups[action.group].status
      return {
        ...state,
        rollups,
      }
    }

    case 'app.setRollupsStatus': {
      // console.log('%capp.setRollupsStatus action', "background: #fa4fd5ff", action)
      const rollups = { ...state.rollups }
      Object.entries(action.statusByGroup).forEach(([group, status]) => rollups[group].status = status)
      return {
        ...state,
        rollups,
      }
    }

    default: {
      return state;
    }
  }
}

export const actions = {
  activityActive: () => ({ type: 'app.activity.active' }),
  activityIdle: () => ({ type: 'app.activity.idle' }),
  appendFeatures: features => ({ type: 'app.appendFeatures', features }),
  appendSpecialUserFeatures: () => ({ type: 'app.appendSpecialUserFeatures' }),
  changePassword: email => ({ type: 'app.changePassword.start', email }),
  clearToken: props => ({ ...props, type: 'app.clearToken' }),
  clearUserLogin: () => ({ type: 'app.clearUserLogin' }),
  confirmAccount: form => ({ type: 'app.confirmAccount.start', form }),
  confirmAccountError: error => ({ type: 'app.confirmAccount.error', error }),
  deleteMe: form => ({ type: 'app.deleteMe.start', form }),
  deleteMeError: error => ({ type: 'app.deleteMe.error', error }),
  disconnect: () => ({ type: 'app.disconnect' }),
  setRollupsStatus: statusByGroup => ({ type: 'app.setRollupsStatus', statusByGroup }),
  forgotPassword: form => ({ type: 'app.forgotPassword.start', form }),
  forgotPasswordError: error => ({ type: 'app.forgotPassword.error', error }),
  getSpecialUserFeatures: specialUser => ({ type: 'app.getSpecialUserFeatures', specialUser }),
  goodbye: props => ({ ...props, type: 'app.goodbye' }),
  initSpecialUserFeatures: specialUserFeatures => ({ type: 'app.initSpecialUserFeatures', specialUserFeatures }),
  loaded: () => ({ type: 'app.loaded' }),
  login: props => ({ ...props, type: 'app.login.start' }),
  loginError: error => ({ type: 'app.login.error', error }),
  logout: props => ({ ...props, type: 'app.logout' }),
  networkDown: () => ({ type: 'app.network.down' }),
  networkPending: () => ({ type: 'app.network.pending' }),
  networkUp: () => ({ type: 'app.network.up' }),
  register: form => ({ type: 'app.register.start', form }),
  registerError: error => ({ type: 'app.register.error', error }),
  resendCode: () => ({ type: 'app.resendCode.start' }),
  resendCodeResult: resultMsg => ({ type: 'app.resendCode.result', resultMsg }),
  resendResetCode: () => ({ type: 'app.resendResetCode.start' }),
  resendResetCodeResult: resultMsg => ({ type: 'app.resendResetCode.result', resultMsg }),
  toggleRollup: group => ({ type: 'app.toggleRollup', group }),
  saveToken: token => ({ type: 'app.saveToken', token }),
  saveUserLogin: (email, password) => ({ type: 'app.saveUserLogin', email, password }),
  setIdleLoggedOut: status => ({ type: 'app.idleLoggedOut', status }),
  setNewPassword: form => ({ type: 'app.setNewPassword.start', form }),
  setNewPasswordError: error => ({ type: 'app.setNewPassword.error', error }),
  setPage: page => ({ type: 'app.setPage', page }),
  start: () => ({ type: 'app.start' }),
  timeToLogout: logoutSeconds => ({ type: 'app.logout.seconds', logoutSeconds }),
  userDetailsChanged: form => ({ type: 'app.userDetailsChanged.start', form }),
  userDetailsChangedError: error => ({ type: 'app.userDetailsChanged.error', error }),
};

export const selectors = {
  isLoaded: state => state.loaded === true,
  isLoggedIn: state => !!state.token,
  getAuthToken: state => state.token && state.token.idToken,
  getNetworkStatus: state => state.networkStatus,
  getActivityStatus: state => state.activityStatus,
  getTimeToLogout: state => state.timeToLogout,
};

// Note: these all use state.app.* rather than just state.*
export const localSelectors = {
  isLoggedIn: state => !!state.app.token,
  getFullToken: state => state.app.token,
  getActivityStatus: state => state.app.activityStatus,
  getTimeToLogout: state => state.app.timeToLogout,
  getUserLogin: state => ({ username: state.app.usr, password: state.app.pwd })
};

function* watchAppStart() {
  // console.log('watchAppStart actions:', actions)
  yield take('app.start');

  try {
    // Initialise the specialUserFeatures from the backend
    // NB: "UrlTrial" not "The350Club", as the backend returns no 350 Club features while 350 Club access is disabled
    yield put(actions.getSpecialUserFeatures("UrlTrial"))

    const token = JSON.parse(localStorage.getItem(TOKEN_KEY));
    if (!token) {
      throw new Error();
    }

    const urlProps = getUrlProps()

    // if there is a token, try to refresh it on app start
    var { refreshResult, timeout } = yield race({
      refreshResult: yield call(api.auth.refresh, { RefreshToken: token.refreshToken, ...urlProps }),
      timeout: yield call(delay, 2000)
    });
    if (timeout) {
      throw new Error();
    } else {
      refreshResult.refreshToken = token.refreshToken; // the refresh result does not include the original refresh token
    }

    yield put(actions.saveToken(refreshResult));

  } catch (e) {
    localStorage.removeItem(TOKEN_KEY);
  } finally {
    yield put(actions.loaded());
  }
}

const getUrlProps = () => {
  // Returns an object containing The350Club and UrlTrial booleans acquired from the URL
  const params = new URLSearchParams(window.location.search)
  const trialDaysLeft = getUrlTrialDaysLeft(params.get("trial"))

  // NB: Props are camel-case to match backend RefreshTokenDto vars
  // (Actaully, urlTrial > UrlTrial OK. But the350club > The350club, not The350Club! )
  return {
    The350Club: params.get("source") === JARGON.THE350CLUB,
    UrlTrial: trialDaysLeft > 0,
  }
}

function* watchLogin() {
  while (true) {
    const props = yield take('app.login.start')
    // console.log('app.login.start props:', props)
    const { form, path, the350club, urlTrial } = props
    yield put(startSubmit('login'))

    try {
      const { token, timeout } = yield race({
        token: call(api.auth.login, form.email, form.password, the350club, urlTrial),
        timeout: call(delay, 15000)
      })

      if (timeout) {
        throw new TimeoutException('Login request timed out.');
      }

      resetCheckboxDefaults()
      localStorage.setItem(TOKEN_KEY, JSON.stringify(token));
      yield put(actions.saveToken(token))
      yield put(stopSubmit('login'))

      // Add date so that we don't use the cached root path
      history.push(`${path}?refresh=${new Date().getTime()}`)
    } catch (e) {

      yield put(stopSubmit('login'));

      if (e.message === 'User is not confirmed.') {
        yield put(actions.saveUserLogin(form.email, form.password));
        history.push(`/${PATH.CONFIRM}`);
      }
      else {
        history.push(`/${PATH.LOGIN}`);
        yield put(actions.loginError(e.message));
      }
    }
  }
}

function* watchUpdateUser() {
  while (true) {
    const { form } = yield take('app.userDetailsChanged.start');
    yield put(startSubmit('myAccountForm'));
    try {
      const { timeout } = yield race({
        token: call(api.updateUser, form),
        timeout: call(delay, 15000)
      });
      if (timeout) {
        throw new TimeoutException('updateUser request timed out.');
      }
      yield put(userActions.saveUser(form));

      //yield put(actions.userDetailsChangedError('changes saved'));
      yield put(stopSubmit('myAccountForm'));
    } catch (e) {
      yield put(stopSubmit('myAccountForm'));
      yield put(actions.userDetailsChangedError(e.message()));
    }
  }
}

function* watchForgotPassword() {
  while (true) {
    const { form } = yield take('app.forgotPassword.start');
    yield put(startSubmit('forgotPassword'));
    yield put(actions.saveUserLogin(form.email, ''));
    try {
      const { timeout } = yield race({
        response: call(api.auth.forgotPassword, form.email),
        timeout: call(delay, 15000)
      });
      if (timeout) {
        throw new TimeoutException('Reset request timed out.');
      }

      // After registration jump to set new password screen
      history.push(`/${PATH.SET_NEW_PASSWORD}`);

      yield put(stopSubmit('forgotPassword'));
    } catch (e) {
      yield put(stopSubmit('forgotPassword'));
      yield put(actions.forgotPasswordError(e.message));
    }
  }
}

function* watchChangePassword() {
  while (true) {
    const { email } = yield take('app.changePassword.start');
    try {
      const { timeout } = yield race({
        response: call(api.auth.forgotPassword, email),
        timeout: call(delay, 15000)
      });
      if (timeout) {
        throw new TimeoutException('Change password send code request timed out.');
      }

      // After requesting code, jump to set new password screen
      history.push(`/${PATH.SET_NEW_PASSWORD}`);

    } catch (e) {
      yield put(actions.setNewPasswordError(e.message));
    }
  }
}

function* watchSetNewPassword() {
  while (true) {
    const { form } = yield take('app.setNewPassword.start');
    let user = yield select(localSelectors.getUserLogin);
    if (!user.username) {
      user = { username: yield select(state => userSelectors.getEmail(state.user)) };
    }

    yield put(startSubmit('setNewPassword'));

    if (!user.username) {
      // handle case where the email address is missing
      yield put(actions.forgotPasswordError('Please re-enter your email address.'));
      history.push(`/${PATH.FORGOT_PASSWORD}`);
      yield put(stopSubmit('setNewPassword'));
      continue;
    }

    try {
      const { timeout } = yield race({
        response: call(api.auth.setNewPassword, user.username, form.code, form.password),
        timeout: call(delay, 15000)
      });
      if (timeout) {
        throw new TimeoutException('Reset request timed out.');
      }

      // After confirming, we want to log in
      const { token, loginTimeout } = yield race({
        token: call(api.auth.login, user.username, form.password),
        loginTimeout: call(delay, 15000)
      });
      if (loginTimeout) {
        throw new TimeoutException('Login request timed out.');
      }
      resetCheckboxDefaults()
      localStorage.setItem(TOKEN_KEY, JSON.stringify(token));
      yield put(actions.saveToken(token));
      history.push('/'); // jump to the home page
      // end

      yield put(stopSubmit('setNewPassword'));
    } catch (e) {
      yield put(stopSubmit('setNewPassword'));
      yield put(actions.setNewPasswordError(e.message));
      yield put(actions.loginError(e.message));
    }
  }
}

function* watchResendResetCode() {
  while (true) {
    yield take('app.resendResetCode.start');
    const user = yield select(localSelectors.getUserLogin);

    if (!user.username) {
      // handle case where the email address is missing
      yield put(actions.forgotPasswordError('Please re-enter your email address.'));
      history.push(`/${PATH.FORGOT_PASSWORD}`);
      return;
    }

    try {
      const { timeout } = yield race({
        token: call(api.auth.forgotPassword, user.username),
        timeout: call(delay, 15000)
      });
      if (timeout) {
        throw new TimeoutException('resend timed out.');
      }
      yield put(actions.resendResetCodeResult('code resent'));
    } catch (e) {
      yield put(actions.resendResetCodeResult(e.message));
    }
  }
}

function* watchResendCode() {
  while (true) {
    yield take('app.resendCode.start');
    const user = yield select(localSelectors.getUserLogin);

    if (!user.username) {
      yield put(actions.resendCodeResult('Unable to resend the code - please register again.'));
      continue;
    }

    try {
      const { timeout } = yield race({
        token: call(api.auth.resendConfCode, user.username),
        timeout: call(delay, 15000)
      });
      if (timeout) {
        throw new TimeoutException('resend timed out.');
      }
      yield put(actions.resendCodeResult('code resent'));
    } catch (e) {
      yield put(actions.resendCodeResult(e.message));
    }
  }
}

function* watchLogout() {
  while (true) {
    const props = yield take('app.logout')
    // console.log('app.logout props:', props)
    const { the350club, urlTrial } = props
    localStorage.removeItem(TOKEN_KEY)
    yield put(actions.clearToken({ the350club, urlTrial }))
    history.push(`/${PATH[BASE.SEARCH]}`)
  }
}

function* watchGoodbye() {
  while (true) {
    const props = yield take('app.goodbye')
    // console.log('app.goodbye props:', props)
    const { the350club, urlTrial } = props
    localStorage.removeItem(TOKEN_KEY)
    yield put(actions.clearToken({ the350club, urlTrial }))
    history.push(`/${PATH.GOODBYE}`)
  }
}

function* watchRegister() {
  while (true) {
    const { form } = yield take('app.register.start');
    yield put(startSubmit('register'));
    yield put(actions.saveUserLogin(form.email, form.password));
    try {
      const { registerTimeout } = yield race({
        response: call(api.register, form),
        registerTimeout: call(delay, 5000000)
      });
      if (registerTimeout) {
        throw new TimeoutException('Register request timed out.');
      }

      // After registration jump to confirmation
      history.push(`/${PATH.CONFIRM}`);

      yield put(stopSubmit('register'));
    } catch (e) {
      yield put(stopSubmit('register'));
      yield put(actions.registerError(e.message));
    }

  }
}

function* watchDeleteMe() {
  while (true) {
    yield take('app.deleteMe.start');
    try {
      const { deleteTimeout } = yield race({
        response: call(api.deleteMe),
        deleteTimeout: call(delay, 3000000)
      });
      if (deleteTimeout) {
        throw new TimeoutException('Delete user request timed out.');
      }

      yield put(actions.goodbye());
    } catch (e) {

    }
  }
}

function* watchConfirmAccount() {
  while (true) {
    const { form } = yield take('app.confirmAccount.start');

    const user = yield select(localSelectors.getUserLogin);

    yield put(startSubmit('confirm'));

    try {
      const { confirmTimeout } = yield race({
        response: call(api.auth.confirmAccount, form, user.username),
        confirmTimeout: call(delay, 15000)
      });
      if (confirmTimeout) {
        throw new TimeoutException('Confirm account request timed out.');
      }

      // After confirming, we want to log in
      const { token, loginTimeout } = yield race({
        token: call(api.auth.login, user.username, user.password),
        loginTimeout: call(delay, 15000)
      });
      if (loginTimeout) {
        throw new TimeoutException('Login request timed out.');
      }
      resetCheckboxDefaults()
      localStorage.setItem(TOKEN_KEY, JSON.stringify(token));
      yield put(actions.saveToken(token));
      // end

      yield put(stopSubmit('confirm'));
    } catch (e) {
      yield put(stopSubmit('confirm'));
      yield put(actions.confirmAccountError(e.message));
    }

  }
}

function* watchNetworkStatus() {
  while (true) {

    try {
      yield put(actions.networkPending());
      const { timeout } = yield race({
        ping: yield call(api.ping),
        timeout: yield call(delay, 2000)
      });
      if (timeout) {
        yield put(actions.networkDown());
      } else {
        yield put(actions.networkUp());
      }
    } catch (e) {
      yield put(actions.networkDown());
    }

    yield call(delay, 3000);
  }
}

function* watchTokenStatus() {
  var nextCheck = 10000;
  while (true) {
    try {

      yield call(delay, nextCheck);

      const token = yield select(localSelectors.getFullToken);
      if (!token) {
        throw new Error();
      }
      const urlProps = getUrlProps()

      var { refreshResult, timeout } = yield race({
        refreshResult: yield call(api.auth.refresh, { RefreshToken: token.refreshToken, ...urlProps }),
        timeout: yield call(delay, 2000)
      });
      if (timeout) {
        throw new Error();
      } else {
        refreshResult.refreshToken = token.refreshToken; // the refresh result does not include the original refresh token
      }

      nextCheck = 1000 * refreshResult.expiresIn / 5;

      yield put(actions.saveToken(refreshResult));
    } catch (e) {
      nextCheck = 10000;
    }
  }
}

function* watchActive() {
  while (true) {
    yield take('app.activity.active');
    yield put(actions.timeToLogout(LOGOUT_WARNING_TIME_SECONDS));
  }
}

function* handleIdle(action) {
  // console.log('handleIdle(): action', action)
  var isLoggedIn = yield select(localSelectors.isLoggedIn)

  if (!isLoggedIn)
    return

  var activity;
  do {
    var time = yield select(localSelectors.getTimeToLogout);
    if (time < 1) {
      yield put(actions.setIdleLoggedOut(true))
      yield put(actions.logout());
      break;
    } else
      yield put(actions.timeToLogout(--time));

    yield call(delay, 1000)
    // console.log("do while (activity ===  ACTIVITY_STATUS.IDLE):", activity ===  ACTIVITY_STATUS.IDLE)
    activity = yield select(localSelectors.getActivityStatus);
  } while (activity === ACTIVITY_STATUS.IDLE)
}

export function* watchIdle() {
  yield takeEvery('app.activity.idle', handleIdle)
}

function* watchGetSpecialUserFeatures() {
  while (true) {
    const props = yield take('app.getSpecialUserFeatures')
    // console.log('getSpecialUserFeatures props:', props)
    const { specialUser } = props

    try {
      // Get the SpecialUserFeatures from backend via api
      const features = yield call(api.auth.getSpecialUserFeatures, specialUser)

      // Save the getSpecialUserFeatures
      yield put(actions.initSpecialUserFeatures(features))

      // If 350 club or trial, append specialUserFeatures to features
      const params = new URLSearchParams(window.location.search)
      const trialDaysLeft = getUrlTrialDaysLeft(params.get("trial"))

      if (params.get("source") === JARGON.THE350CLUB || trialDaysLeft > 0) {

        yield put(actions.setRollupsStatus(defaultRollupStatus()))

        initialState.rollupsSet = true
        yield put(actions.appendSpecialUserFeatures())
      }
    } catch (e) {
      console.error('getSpecialUserFeatures failed:', e)
    }
  }
}

const groupEnabled = ({ features, hook }) =>
  (CHECKBOX_GROUP_PROPS[hook].LEVEL === CHECKBOX_LEVEL.PREMIUM && features.PremiumCheckboxes) ||
  (CHECKBOX_GROUP_PROPS[hook].LEVEL === CHECKBOX_LEVEL.STANDARD && features.StandardCheckboxes)

function* watchSaveToken() {
  while (true) {
    const { token } = yield take('app.saveToken')
    // console.log('watchSaveToken token:', token)
    // const features = token.features.reduce((o, f) => { o[f] = true; return o; }, {})

    if (!initialState.rollupsSet) {
      yield put(actions.setRollupsStatus(defaultRollupStatus()))

      initialState.rollupsSet = true
    }
  }
}

export function* sagas() {
  yield all([
    watchActive(),
    watchAppStart(),
    watchChangePassword(),
    watchConfirmAccount(),
    watchDeleteMe(),
    watchForgotPassword(),
    watchGetSpecialUserFeatures(),
    watchGoodbye(),
    watchIdle(),
    watchLogin(),
    watchLogout(),
    watchNetworkStatus(),
    watchRegister(),
    watchResendCode(),
    watchResendResetCode(),
    watchSaveToken(),
    watchSetNewPassword(),
    watchTokenStatus(),
    watchUpdateUser(),
  ]);
}
